import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { RefreshCw, Search, FileDown, PackageX, Store, Layers, Copy } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';

const API_BASE = 'https://api.glowytics.com';
const PAGE = 200;

const fmt = (v) =>
  v == null ? '—' : `₺${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
const GENDER = { M: 'Men', W: 'Women', C: 'Kids', U: 'Unisex' };

/**
 * Products competitors sell that we don't carry — same brands and same product groups as
 * our own catalog. One row per style+colour code; a product sold by several competitors is
 * one row with a price per seller. Data: GET /api/competitor-intel/missing-products
 * (built daily by scripts/match_kixz_competitors.sql).
 */
export default function CompetitorMissingProducts() {
  const { selectedBrand } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [group, setGroup] = useState('');
  const [brand, setBrand] = useState('');
  const [site, setSite] = useState('');
  const [multiOnly, setMultiOnly] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(true);
  const [hideSimilar, setHideSimilar] = useState(false);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/competitor-intel/missing-products`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand?.id || localStorage.getItem('currentBrandId') || '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message);
      toast.error('Could not load missing products. ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setLimit(PAGE); }, [group, brand, site, multiOnly, inStockOnly, hideSimilar, search]);

  const all = useMemo(() => data?.rows || [], [data]);
  const SITES = data?.compSites || [];

  // Everything except the group filter — the group cards show what each group would leave.
  const baseRows = useMemo(() => {
    let r = all;
    if (brand) r = r.filter((x) => x.brand === brand);
    if (site) r = r.filter((x) => x.offers?.[site]);
    if (multiOnly) r = r.filter((x) => x.nSites > 1);
    if (inStockOnly) r = r.filter((x) => x.inStock);
    if (hideSimilar) r = r.filter((x) => !x.similarOwned);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter((x) => (x.name || '').toLowerCase().includes(q) || (x.code || '').toLowerCase().includes(q));
    }
    return r;
  }, [all, brand, site, multiOnly, inStockOnly, hideSimilar, search]);

  const rows = useMemo(() => (group ? baseRows.filter((x) => x.group === group) : baseRows), [baseRows, group]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const r of baseRows) {
      const g = m.get(r.group) || { group: r.group, n: 0, multi: 0 };
      g.n += 1;
      if (r.nSites > 1) g.multi += 1;
      m.set(r.group, g);
    }
    return [...m.values()].sort((a, b) => b.n - a.n);
  }, [baseRows]);
  const maxGroup = groups[0]?.n || 1;

  const brands = useMemo(() => [...new Set(all.map((x) => x.brand))].sort(), [all]);

  const kpi = useMemo(() => {
    const k = { total: rows.length, multi: 0, similar: 0, bySite: Object.fromEntries(SITES.map((s) => [s, 0])) };
    for (const r of rows) {
      if (r.nSites > 1) k.multi += 1;
      if (r.similarOwned) k.similar += 1;
      for (const s of r.sites || []) if (s in k.bySite) k.bySite[s] += 1;
    }
    return k;
  }, [rows, SITES]);

  const exportCSV = useCallback(() => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const head = ['Code', 'Brand', 'Group', 'Product', 'Gender', 'Sellers', 'Min price', 'List price',
      'Similar model in our range', ...SITES.flatMap((s) => [`${s} price`, `${s} URL`])];
    const lines = rows.map((r) => [
      r.code, r.brand, r.group, r.name, GENDER[r.gender] || '', r.nSites, r.minPrice ?? '', r.maxListPrice ?? '',
      r.similarOwned ? 'yes' : 'no',
      ...SITES.flatMap((s) => [r.offers?.[s]?.price ?? '', r.offers?.[s]?.url ?? '']),
    ].map(esc).join(','));
    const blob = new Blob(['﻿' + [head.map(esc).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `missing-products-${(data?.ourSite || 'brand').toLowerCase()}-${data?.fetchDate || ''}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [rows, SITES, data]);

  if (loading) {
    return (
      <Card className="rounded-2xl border border-gray-100 shadow-sm p-10 flex items-center justify-center text-gray-500">
        <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Loading…
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="rounded-2xl border border-red-100 shadow-sm p-8 text-red-600">
        Error: {error}
        <button onClick={fetchData} className="ml-3 underline">Try again</button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI + group breakdown */}
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="p-5 bg-white">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Not in our catalog</h2>
              <p className="text-sm text-gray-500 max-w-3xl">
                Products {SITES.join(', ')} sell that {data?.ourSite} doesn&apos;t — limited to our brands and
                our product groups. A product is counted as ours if it matches by barcode, style code or
                model + colour. Data: {data?.fetchDate || '—'}.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={exportCSV}
                disabled={!rows.length}
                className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                <FileDown className="w-4 h-4" /> Download CSV
              </button>
              <button
                type="button" onClick={fetchData} title="Refresh"
                className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat icon={PackageX} color="text-indigo-600 bg-indigo-50" label="Missing products" value={fmtNum(kpi.total)}
              sub={`of ${fmtNum(all.length)} before filters`} />
            <Stat icon={Store} color="text-red-600 bg-red-50" label="Sold by 2+ competitors" value={fmtNum(kpi.multi)}
              valueColor="text-red-600" sub="strongest signal of demand" />
            <Stat icon={Copy} color="text-amber-600 bg-amber-50" label="Similar model in our range" value={fmtNum(kpi.similar)}
              sub="other colour / version of a model we sell" />
            <Stat icon={Layers} color="text-gray-600 bg-gray-100" label="By seller"
              value={SITES.map((s) => fmtNum(kpi.bySite[s])).join(' · ')} sub={SITES.join(' · ')} />
          </div>

          {/* Product groups — click to filter */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-1.5">
            {groups.map((g) => (
              <button
                key={g.group} type="button"
                onClick={() => setGroup(group === g.group ? '' : g.group)}
                className={`text-left rounded-lg px-2 py-1.5 ${group === g.group ? 'bg-indigo-50 ring-1 ring-indigo-200' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium text-gray-800">{g.group}</span>
                  <span className="tabular-nums text-gray-600">
                    {fmtNum(g.n)} <span className="text-xs text-gray-400">· {fmtNum(g.multi)} on 2+</span>
                  </span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${(g.n / maxGroup) * 100}%` }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Filters + table */}
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <select value={group} onChange={(e) => setGroup(e.target.value)} className="border border-gray-300 rounded-lg px-3 h-9 text-sm bg-white">
            <option value="">All groups</option>
            {groups.map((g) => <option key={g.group} value={g.group}>{g.group} ({g.n})</option>)}
          </select>
          <select value={brand} onChange={(e) => setBrand(e.target.value)} className="border border-gray-300 rounded-lg px-3 h-9 text-sm bg-white">
            <option value="">All brands</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={site} onChange={(e) => setSite(e.target.value)} className="border border-gray-300 rounded-lg px-3 h-9 text-sm bg-white">
            <option value="">All sellers</option>
            {SITES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search: product name, code…"
              className="border border-gray-300 rounded-lg pl-9 pr-3 h-9 text-sm w-64 bg-white"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)} /> In stock somewhere
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={multiOnly} onChange={(e) => setMultiOnly(e.target.checked)} /> 2+ sellers
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={hideSimilar} onChange={(e) => setHideSimilar(e.target.checked)} /> Hide models we already sell
          </label>
          <span className="text-sm text-gray-400 ml-auto tabular-nums">{fmtNum(rows.length)} rows</span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white sticky top-0 z-10 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-gray-700 sticky left-0 bg-white z-20">Product</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Group</th>
                {SITES.map((s) => <th key={s} className="text-right px-4 py-3 font-semibold text-gray-700">{s}</th>)}
                <th className="text-right px-4 py-3 font-semibold text-gray-500">Lowest</th>
                <th className="text-right px-5 py-3 font-semibold text-gray-500">List price</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, limit).map((r) => (
                <tr key={r.code} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-5 py-2 sticky left-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                      {r.image ? (
                        <img src={r.image} alt="" className="w-10 h-10 object-cover rounded-lg border border-gray-200" loading="lazy" />
                      ) : <div className="w-10 h-10 rounded-lg bg-gray-100" />}
                      <div className="min-w-[240px] max-w-[380px]">
                        <div className="font-medium text-gray-900 leading-tight">{r.name}</div>
                        <div className="text-xs text-gray-400 flex flex-wrap items-center gap-x-1.5">
                          <span>{r.brand}</span>
                          {r.code && <span>· {r.code.toUpperCase()}</span>}
                          {r.gender && <span>· {GENDER[r.gender] || r.gender}</span>}
                          {r.similarOwned && (
                            <span className="text-amber-600" title="We sell another colour or version of this model">· similar in our range</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{r.group}</td>
                  {SITES.map((s) => {
                    const o = r.offers?.[s];
                    return (
                      <td key={s} className="text-right px-4 py-2 tabular-nums">
                        {o ? (
                          <div className="leading-tight">
                            <a href={o.url} target="_blank" rel="noreferrer"
                              className={`hover:underline ${o.price === r.minPrice ? 'text-emerald-700 font-semibold' : 'text-gray-700'}`}>
                              {fmt(o.price)}
                            </a>
                            {o.inStock === false && <div className="text-[11px] text-red-500">out of stock</div>}
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                    );
                  })}
                  <td className="text-right px-4 py-2 tabular-nums font-medium text-gray-800">{fmt(r.minPrice)}</td>
                  <td className="text-right px-5 py-2 tabular-nums text-gray-500">{fmt(r.maxListPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="p-8 text-center text-gray-400">No records found.</div>}
          {rows.length > limit && (
            <div className="p-4 text-center">
              <button onClick={() => setLimit((l) => l + PAGE)} className="text-sm text-indigo-600 font-medium hover:underline">
                Show {fmtNum(Math.min(PAGE, rows.length - limit))} more
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function Stat({ icon: Icon, color, label, value, sub, valueColor = 'text-gray-900' }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500 truncate">{label}</div>
        <div className={`text-lg font-bold tabular-nums truncate ${valueColor}`}>{value}</div>
        {sub && <div className="text-[11px] text-gray-400 truncate">{sub}</div>}
      </div>
    </div>
  );
}
